import { inject, Injectable } from '@angular/core';
import { PRIMARY_OUTLET, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import {
  AppLocale,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  resolveInitialLocale,
} from './locale.constants';
import {
  localeFromRouteSegment,
  localizePath,
  routeSegmentForLocale,
  stripLocaleRouteSegment,
} from './localized-route.utils';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  initialize(): Promise<void> | void {
    const locale = resolveInitialLocale();
    this.applyLocale(locale, false);

    if (typeof window === 'undefined') {
      return;
    }

    return firstValueFrom(this.transloco.load(locale)).then(() => undefined);
  }

  setLocale(locale: AppLocale): void {
    this.applyLocale(locale, true);
    void this.navigateToLocale(locale);
  }

  setRouteLocale(locale: AppLocale): void {
    this.applyLocale(locale, true);
  }

  localizedPath(path: string): string {
    return localizePath(path, this.currentLocale);
  }

  private applyLocale(locale: AppLocale, persist: boolean): void {
    this.transloco.setActiveLang(locale);
    if (persist && typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
    this.syncDocumentLang(locale);
  }

  get currentLocale(): AppLocale {
    const lang = this.transloco.getActiveLang();
    return (lang as AppLocale) || DEFAULT_LOCALE;
  }

  private syncDocumentLang(locale: AppLocale): void {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale === 'pt-BR' ? 'pt-BR' : 'en';
    }
  }

  private navigateToLocale(locale: AppLocale): Promise<boolean> {
    const urlTree = this.router.parseUrl(this.router.url);
    const primarySegments = urlTree.root.children[PRIMARY_OUTLET]?.segments.map(
      (segment) => segment.path,
    ) ?? [];
    const currentLocale = localeFromRouteSegment(primarySegments[0]);
    const routeSegments = currentLocale
      ? primarySegments.slice(1)
      : stripLocaleRouteSegment(primarySegments);
    const localeSegment = routeSegmentForLocale(locale);
    const commands = ['/', ...(localeSegment ? [localeSegment] : []), ...routeSegments];

    return this.router.navigate(commands, {
      fragment: urlTree.fragment ?? undefined,
      queryParams: urlTree.queryParams,
    });
  }
}
